// Señales de INTENSIDAD de uso del operador (Item B2-emisión) + contador de core-actions
// per-usuario que consume el feedback-por-uso del preview (Item E#3).
//
// Dos responsabilidades, una sola superficie para no duplicar el concepto de "acción core":
//   1. B2 — emitir a GA4 (reusando el sink de analytics) cada acción core de una feature con
//      `core_action=true` + `feature_key` + `by_user_id`, y un heartbeat de minutos activos.
//      Permite al dashboard identificar al "heavy daily operator" y ponderar después el
//      feedback por intensidad, sliceando por feature.
//   2. E#3 — llevar un contador per-usuario-por-feature (per-tab, en memoria) de core-actions
//      para disparar el prompt de feedback OMITIBLE tras N acciones reales. NO se persiste como
//      variante: es una señal de uso, no estado de rollout.
//
// GENÉRICO por feature: `recordCoreAction(featureKey, action)` es la entrada canónica; el
// contador va keyed por `${userId}:${featureKey}` para que DOS features del framework nunca
// mezclen conteos (mezclarlos corrompería el `core_action_count` del usage_feedback de cada
// una). `recordMenuCoreAction` queda como wrapper de compatibilidad para los call-sites de menú.
//
// SIN PII: solo `by_user_id` (el id de usuario que el resto de telemetría ya emite) + el tipo de
// acción. Nunca nombre, email ni contenido del producto. Si el sink está apagado (no-prod / sin
// tracking), `ga4Event` es no-op y nada rompe.

import { ga4Event } from '@utils/analytics/ga4';
import { getStoreSnapshot } from './store';
import { isPreviewActive } from './preview';
import { MENU_FEATURE_KEY, type FeatureKey } from './constants';

// Tipos de acción core del menú. Estables (viajan en el payload GA4, no se traducen).
export type MenuCoreActionType = 'product_create' | 'product_edit' | 'product_reorder';
// Alias histórico (cuando solo existía el menú). Preferir `MenuCoreActionType` en código nuevo.
export type CoreActionType = MenuCoreActionType;

// Tipos de acción core de impresión BLE (impresion-ble). Mismas reglas de estabilidad.
export type BleCoreActionType = 'printer_paired' | 'print_attempt' | 'print_success';

// ¿La versión NUEVA de la feature está en pantalla para este usuario? = variante treatment
// adoptada por el comercio O vista previa per-usuario activa. Solo en ese caso una core-action
// es "uso de la versión nueva": las acciones en la versión vieja (control sin preview) NO deben
// contar ni emitir la señal de intensidad (Seer 14512239/1 — evita inflar core_action_count con
// uso de la versión anterior, que corrompería la ponderación del feedback por intensidad y el
// slice por variante).
function isFeatureOnScreen(featureKey: FeatureKey): boolean {
  const variant = getStoreSnapshot(featureKey).entry.variant;
  return variant === 'treatment' || isPreviewActive(featureKey);
}

// Nombre de evento GA4 (snake_case, coherente con el resto del catálogo de rollout).
const GA4_CORE_ACTION_EVENT = 'rollout_core_action';
const GA4_HEARTBEAT_EVENT = 'rollout_active_heartbeat';

// Cadencia del heartbeat de minutos activos: una marca por minuto de actividad real (no por
// tiempo de pestaña abierta inactiva). El consumidor llama `pingActivity()` ante interacción;
// solo emitimos si pasó al menos un minuto desde el último latido.
const HEARTBEAT_INTERVAL_MS = 60_000;

function byUserId(): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem('id_user') ?? '';
  } catch {
    return '';
  }
}

// ── Contador de core-actions per-usuario-por-feature (per-tab, en memoria) ──────────────
// Keyed por `${userId}:${featureKey}`: un cambio de usuario en la misma pestaña (multisucursal)
// no hereda el conteo del anterior, y dos features del framework no se cruzan conteos. No se
// persiste: el prompt de feedback es per-sesión-de-uso.
const coreActionCounts = new Map<string, number>();
type CountListener = (count: number) => void;
const countListeners = new Map<string, Set<CountListener>>();

function countKey(userId: string, featureKey: string): string {
  return `${userId}:${featureKey}`;
}

function listenersFor(featureKey: string): Set<CountListener> {
  let set = countListeners.get(featureKey);
  if (!set) {
    set = new Set();
    countListeners.set(featureKey, set);
  }
  return set;
}

export function getCoreActionCount(featureKey: FeatureKey): number {
  return coreActionCounts.get(countKey(byUserId(), featureKey)) ?? 0;
}

// Notifica PULL, no PUSH: no difundimos el valor del contador (que es del usuario que acaba de
// actuar). Cada listener vuelve a leer getCoreActionCount() para SU usuario activo, evitando que
// el listener del usuario B reciba el conteo del usuario A en una carrera (Seer 14512239/0).
function notifyCount(featureKey: FeatureKey): void {
  const current = getCoreActionCount(featureKey);
  listenersFor(featureKey).forEach((l) => l(current));
}

// Suscribe al contador de core-actions del usuario activo PARA una feature. Devuelve unsubscribe.
export function subscribeCoreActionCount(featureKey: FeatureKey, listener: CountListener): () => void {
  const set = listenersFor(featureKey);
  set.add(listener);
  return () => {
    set.delete(listener);
  };
}

// ── Heartbeat de minutos activos ───────────────────────────────────────────────────────
let lastHeartbeatAt = 0;

// Marca actividad. Emite un latido a GA4 a lo sumo una vez por minuto. Idempotente dentro de
// la ventana: llamarlo en ráfaga no infla el conteo de minutos activos.
export function pingActivity(): void {
  const now = Date.now();
  if (now - lastHeartbeatAt < HEARTBEAT_INTERVAL_MS) return;
  lastHeartbeatAt = now;
  ga4Event(GA4_HEARTBEAT_EVENT, { by_user_id: byUserId() });
}

// ── Registro de una core-action ─────────────────────────────────────────────────────────
// Lo llaman los call-sites de cada feature (hooks de menú, impresión BLE) cuando el operador
// ejecuta una acción de valor con la versión nueva. Solo cuenta como "uso de la versión nueva"
// si la feature está EN PANTALLA (treatment o preview): en control puro no emite señal de
// intensidad ni incrementa el contador (Seer 14512239/1). El heartbeat de minutos activos SÍ se
// marca siempre (mide actividad del operador, no de una versión concreta).
export function recordCoreAction(featureKey: FeatureKey, action: string): void {
  // El heartbeat es agnóstico de versión: cualquier acción es "operador activo".
  pingActivity();

  // De aquí en adelante, solo si la versión nueva está en pantalla: la señal de intensidad y el
  // contador E#3 miden uso de la versión NUEVA (la OFRECIDA), no de la anterior.
  if (!isFeatureOnScreen(featureKey)) return;

  const userId = byUserId();

  // B2 — señal a GA4. El `rollout_variant` ya viaja como user_property global (applyFeatureVersionTags),
  // así el dashboard puede slicear por variante sin que lo repitamos aquí. `feature_key` permite
  // separar las core-actions de cada feature del framework en el mismo evento.
  ga4Event(GA4_CORE_ACTION_EVENT, { core_action: true, action, feature_key: featureKey, by_user_id: userId });

  // E#3 — contador per-usuario-por-feature (per-tab) que dispara el prompt de feedback por uso.
  const key = countKey(userId, featureKey);
  coreActionCounts.set(key, (coreActionCounts.get(key) ?? 0) + 1);
  notifyCount(featureKey);
}

// Wrapper de compatibilidad para los call-sites de menú (hooks compartidos V1/V2 en
// create/edit/reorder de producto). @deprecated en código nuevo: usar
// `recordCoreAction(MENU_FEATURE_KEY, action)` directo.
export function recordMenuCoreAction(action: MenuCoreActionType): void {
  recordCoreAction(MENU_FEATURE_KEY, action);
}

// Solo para tests: limpia el estado de módulo entre casos.
export function __resetUsageSignalsForTests(): void {
  coreActionCounts.clear();
  countListeners.clear();
  lastHeartbeatAt = 0;
}
